import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { useDispatch } from 'react-redux';

import { updateUser } from '../../app/slices/user/userSlice';
import { errorHandler } from '../../services/authService';
import { registerBusinessUser } from '../../services/onboarding';
import { pattern, required } from '../../utils/form-default-errors';
import { EMAIL } from '../..//utils/common-regex';
import logo from '../../assets/ncbce-logo-alt.png';
import { Typography, Button, TextInput, Link } from '../../components';
import * as S from './BusinessSignUpForm.styles';
import { META_TITLE, PASSWORD_REGEX } from '../../../constants/general.constants';

const BusinessSignUpForm = () => {
	const history = useHistory();
	const {
		register,
		handleSubmit,
		getValues,
		formState: { errors, isSubmitting },
	} = useForm();
	const dispatch = useDispatch();

	useEffect(() => {
		// Set document title
		document.title = `Business Sign Up | ${META_TITLE}`;
	}, []);

	async function asyncCaller(data) {
		const {
			data: { user },
		} = await registerBusinessUser({ firstName: data.firstName, lastName: data.lastName, email: data.email, password: data.password });

		dispatch(updateUser(user));

		history.push('/business/create-company');
	}

	function onSubmit(e) {
		handleSubmit(asyncCaller)(e).catch(errorHandler);
	}

	return (
		<S.Wrapper>
			<S.Header>
				<div className="flex-1" />
				<div className="centered">
					<S.Picture>
						<img src={logo} alt="NCBCE logo" />
					</S.Picture>
				</div>
				<div className="flex-1" />
			</S.Header>
			<S.Container>
				<Typography tag="h1" weight="extrablack" center>
					Create Personal Account
				</Typography>
				<Typography tag="p" className="description" center>
					Enter your credentials below.
				</Typography>

				<S.Form onSubmit={onSubmit}>
					<div className="two-items">
						<TextInput
							label="First Name"
							id="first-name"
							error={errors.firstName}
							autoComplete="first-name"
							{...register('firstName', {
								required: required('First Name'),
							})}
						/>

						<TextInput
							label="Last Name"
							id="last-name"
							error={errors.lastName}
							autoComplete="last-name"
							{...register('lastName', {
								required: required('Last Name'),
							})}
						/>
					</div>

					<TextInput
						label="Email"
						id="email"
						error={errors.email}
						autoComplete="email"
						{...register('email', {
							required: required('Email'),
							pattern: pattern('Email', EMAIL),
						})}
					/>

					<TextInput
						label="Password"
						id="password"
						type="password"
						error={errors.password}
						autoComplete="current-password"
						{...register('password', {
							required: required('Password'),
							pattern: pattern('Password', PASSWORD_REGEX, 'Password must contain at least one number, one uppercase letter, one lowercase letter, and be at least 8 characters long'),
						})}
					/>

					<TextInput
						label="Confirm Password"
						id="confirm-password"
						type="password"
						error={errors.confirmPassword}
						autoComplete="confirm-password"
						{...register('confirmPassword', {
							required: required('Confirm Password'),
							pattern: pattern('Confirm Password', PASSWORD_REGEX, 'Confirm Password must contain at least one number, one uppercase letter, one lowercase letter, and be at least 8 characters long'),
							validate: {
								equalToPassword: (v) => (getValues('password') === v ? true : "Confirm Password doesn't match with Password"),
							},
						})}
					/>

					<Button type="submit" className="submit-button">
						<Typography variation="button-medium" weight="bold">
							{isSubmitting ? 'Loading...' : 'Sign Up'}
						</Typography>
					</Button>
				</S.Form>
			</S.Container>

			<Typography tag="h6" className="login-message" center>
				<span>Already have an account?</span>&nbsp;<Link to="/login">Log In</Link>
			</Typography>
		</S.Wrapper>
	);
};

export default BusinessSignUpForm;
